'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { fNumber } from 'src/utils/format/format-number';
import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { RADIUS, SURFACE } from 'src/theme/ui-tokens';

import { CountryTag } from '../country-tag';
import { HubPressable } from './hub-pressable';

/**
 * 순위 한 줄씩 — 순번 · 이름 · 값, 그리고 그 아래 **1등 대비 길이의 막대**.
 *
 * 띠는 **1등 기준 비율**이다(전체 대비가 아니다) — 무엇이 크고 작은지를 한눈에 견주는 것이 목적이다.
 * 🔑 막대는 글자 «뒤»가 아니라 **아래 제 줄**에 둔다 — 글자에 겹치면 이름이 흐려지고 길이도 눈대중이 안 된다.
 * ⚠️ 만 이상은 한국 자릿수로 줄여 적는다(`3.6억`) — 아홉 자리 숫자가 이름을 밀어내면 줄이 깨진다.
 * 🚨 **못 누르는 줄은 카드가 아니다** — 누를 수 있을 때만 `HubPressable` 이 붙고, 그것도 `flat`(면 색)이다.
 *   순위는 네댓 줄씩 쌓이므로 줄마다 테두리를 두르면 상자가 내용보다 먼저 보인다.
 * ⛔ 여기서 따로 테두리를 그리지 마라 — 누를 수 있는 줄은 화면마다 같은 결이어야 한다.
 * `marked` 는 **우리가 이미 보고 있는 것**에 표를 붙인다(추적 중인 브랜드) — 남의 순위에서 내 것을 먼저 찾게.
 * ⚠️ 나라 순위는 `format="country"` 로 넘긴다 — 이름을 **미리 다듬지 말고 코드 그대로** 실어야
 *   국기를 붙일 수 있다(다듬은 「미상」에는 코드가 없다).
 */
export function HubRankingList({
  title,
  rows,
  max = 6,
  onPick,
  marked,
  markLabel = '추적 중',
  format = 'text',
}: {
  /** 안 주면 제목 줄을 그리지 않는다 — 구역 카드가 이름을 이미 쥐고 있을 때다 */
  title?: string;
  rows: { name?: string; label?: string; count?: number }[];
  max?: number;
  /** `country` 면 국기 + 대문자 코드로 적는다(`CountryTag`) — 그때 `rows` 는 코드 그대로다 */
  format?: 'text' | 'country';
  /** 줄을 눌렀을 때 — 없으면 누를 수 없다 */
  onPick?: (name: string) => void;
  /** 표를 붙일 이름(소문자) — 우리가 이미 보고 있는 것을 가려낸다 */
  marked?: Set<string>;
  markLabel?: string;
}) {
  const shown = rows.slice(0, max).map((row) => ({ name: String(row.name ?? row.label ?? ''), count: Number(row.count ?? 0) }));
  const top = shown[0]?.count ?? 0;

  return (
    <Box>
      {title && (
        <Typography variant="uiLabel" color="text.disabled" sx={{ mb: 1 }}>{title}</Typography>
      )}
      {shown.length === 0 && <Typography variant="body2" color="text.secondary">값이 없습니다.</Typography>}
      <Stack spacing={0.25}>
        {shown.map((row, i) => (
          <RankRow key={row.name} onPick={onPick && row.name ? () => onPick(row.name) : undefined}>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
              <Typography variant="uiLabel" color="text.disabled" sx={{ width: 14, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {i + 1}
              </Typography>
              <Typography noWrap component="div" variant="uiStrong" sx={{ flex: 1, minWidth: 0 }}>
                {format === 'country' ? <CountryTag code={row.name} /> : row.name || '—'}
              </Typography>
              {marked?.has(row.name.toLowerCase()) && (
                <Typography
                  noWrap
                  sx={{ flexShrink: 0, px: 0.625, borderRadius: RADIUS.pill, bgcolor: 'primary.lighter', color: 'primary.dark', typography: 'uiLabel', lineHeight: '16px' }}
                >
                  {markLabel}
                </Typography>
              )}
              <Typography noWrap variant="uiNum" color="text.secondary">
                {row.count >= 10000 ? formatCompactNumber(row.count, { unit: 'ko', trimPointZero: true, nonPositive: 'zero' }) : fNumber(row.count)}
              </Typography>
            </Stack>

            {/* 막대 — 1등만 진하고 아래로 갈수록 옅다(순위가 색으로도 읽힌다) */}
            <Box sx={{ height: 4, borderRadius: RADIUS.pill, bgcolor: SURFACE.sunken, overflow: 'hidden' }}>
              <Box
                sx={{
                  height: 1,
                  width: top > 0 ? `${Math.max(2, (row.count / top) * 100)}%` : 0,
                  borderRadius: RADIUS.pill,
                  bgcolor: 'primary.main',
                  opacity: Math.max(0.35, 1 - i * 0.08),
                }}
              />
            </Box>
          </RankRow>
        ))}
      </Stack>
    </Box>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/**
 * 줄 하나의 껍데기 — **누를 수 있을 때만** 누름 줄을 두른다.
 * ⚠️ 못 누르는 줄에 누름 줄을 두르면 「눌리는 줄」로 읽힌다(손짓도 꺾쇠도 없는데 테두리만 있다).
 */
function RankRow({ onPick, children }: { onPick?: () => void; children: ReactNode }) {
  if (!onPick) return <Box sx={{ px: 1, py: 0.5 }}>{children}</Box>;
  return <HubPressable dense flat onPress={onPick}>{children}</HubPressable>;
}
